import { useState, useEffect, useRef } from 'react';

// All 10 screenshots in exact numeric order 1–10
const SCREENS = [
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/d04bkfzl_1.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/frl01wgg_2.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/q6um7znn_3.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/0488d2xx_4.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/4jqcgdr8_5.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/0rb7umst_6.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/rpekvlh2_7.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/f9599jc9_8.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/k0xj5ano_9.PNG',
  'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/7tbymkwh_10.PNG',
];

const TOTAL = 10;

// Phone frame
const PhoneFrame = ({ children, isCenter, w, h }) => {
  const br = Math.round(w * 0.2);
  const pad = Math.round(w * 0.036);
  const sBr = Math.round(w * 0.168);
  const diW = Math.round(w * 0.386);
  const diH = Math.round(w * 0.114);
  const diTop = Math.round(w * 0.064);
  const hiW = Math.round(w * 0.454);

  return (
    <div style={{ position: 'relative', width: w, height: h, background: '#000', borderRadius: br, padding: pad, boxShadow: isCenter ? '0 20px 60px rgba(0,0,0,0.3), 0 8px 20px rgba(0,0,0,0.15)' : '0 8px 30px rgba(0,0,0,0.2)' }}>
      <div style={{ position: 'absolute', top: diTop, left: '50%', transform: 'translateX(-50%)', width: diW, height: diH, background: '#000', borderRadius: 999, zIndex: 20 }} />
      <div style={{ width: '100%', height: '100%', background: '#FAFAF9', borderRadius: sBr, overflow: 'hidden', position: 'relative' }}>
        {children}
      </div>
      <div style={{ position: 'absolute', bottom: Math.max(4, pad - 3), left: '50%', transform: 'translateX(-50%)', width: hiW, height: 3, background: 'rgba(255,255,255,0.3)', borderRadius: 999 }} />
    </div>
  );
};

// Crossfade viewport
const ScreenViewport = ({ activeIdx, reducedMotion }) => (
  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
    {SCREENS.map((src, i) => (
      <img
        key={i}
        src={src}
        alt={`App screen ${i + 1}`}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: (reducedMotion ? 0 : activeIdx) === i || (reducedMotion && i === 0) ? 1 : 0, transition: reducedMotion ? 'none' : 'opacity 400ms ease' }}
        draggable={false}
      />
    ))}
  </div>
);

export const SeeItInActionSection = () => {
  const [idx, setIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const intervalRef = useRef(null);

  // Preload all images
  useEffect(() => {
    SCREENS.forEach(src => { const img = new Image(); img.src = src; });
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mql.matches);
    const h = (e) => setReducedMotion(e.matches);
    mql.addEventListener('change', h);
    return () => mql.removeEventListener('change', h);
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Auto-advance every 1250ms
  useEffect(() => {
    if (reducedMotion) return;
    intervalRef.current = setInterval(() => setIdx(p => (p + 1) % TOTAL), 1250);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [reducedMotion]);

  // Phone sizes
  const centerW = isMobile ? Math.min(Math.round(window.innerWidth * 0.62), 300) : 220;
  const centerH = Math.round(centerW * 2.05);
  const fanH = isMobile ? centerH + 60 : 600;

  // Background phones config — hidden on mobile
  const bgPhones = isMobile ? [] : [
    { id: 'lb1', rotate: -18, scale: 0.86, z: 1, opacity: 0.20, blur: 1.5, offset: 1, dx: -270, dy: 50 },
    { id: 'lb2', rotate: -10, scale: 0.90, z: 2, opacity: 0.26, blur: 1, offset: 2, dx: -165, dy: 25 },
    { id: 'rb2', rotate: 10, scale: 0.90, z: 2, opacity: 0.26, blur: 1, offset: 3, dx: 165, dy: 25 },
    { id: 'rb1', rotate: 18, scale: 0.86, z: 1, opacity: 0.20, blur: 1.5, offset: 4, dx: 270, dy: 50 },
  ];

  return (
    <section
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: '#fff',
        paddingTop: isMobile ? 48 : 64,
        paddingBottom: isMobile ? 80 : 140,
      }}
      data-testid="see-it-in-action-section"
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px' }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 32 : 56 }} data-testid="see-it-headline">
          <p style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.18em', color: '#880808', textTransform: 'uppercase', marginBottom: 8 }}>
            BUILT FOR EVERY KIND OF SOLO OPERATOR
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 800, color: '#36454F', lineHeight: 1.1, marginBottom: 12 }}>
            WHATEVER YOU DO, RUN IT ON SERVITIUM.
          </h2>
          <p style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 400, fontSize: '1rem', color: '#6b7280', maxWidth: 620, margin: '0 auto' }}>
            Every solo operator gets the same infrastructure — verified identity, organized jobs, and a track record customers can see.
          </p>
        </div>

        {/* Phone fan cluster */}
        <div
          style={{ position: 'relative', width: '100%', height: fanH, overflow: 'visible' }}
          data-testid="phone-fan-cluster"
        >
          {/* Background phones (desktop only) */}
          {bgPhones.map((p) => (
            <div
              key={p.id}
              style={{
                position: 'absolute',
                left: '50%',
                top: '54%',
                transform: `translate(-50%, -50%) translateX(${p.dx}px) translateY(${p.dy}px) rotate(${p.rotate}deg) scale(${p.scale})`,
                zIndex: p.z,
                opacity: p.opacity,
                filter: `blur(${p.blur}px)`,
              }}
            >
              <PhoneFrame w={220} h={450}>
                <ScreenViewport activeIdx={(idx + p.offset) % TOTAL} reducedMotion={reducedMotion} />
              </PhoneFrame>
            </div>
          ))}

          {/* Center phone */}
          <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 5 }}>
            <PhoneFrame isCenter w={centerW} h={centerH}>
              <ScreenViewport activeIdx={idx} reducedMotion={reducedMotion} />
            </PhoneFrame>
          </div>
        </div>
      </div>
    </section>
  );
};
